import React, { useEffect, useState } from "react";
import { Formik, useFormik } from "formik";
import {
  Card,
  CardBody,
  Label,
  Input,
  FormFeedback,
  Row,
  Col,
  Form,
} from "reactstrap";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import Dropzone from "react-dropzone";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import QrThemeService from "../../../Services/QRCodeServices/QrThemeService/QrThemeService";

// Utility function to format file sizes
const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

const AddUpdateTheme = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingImage, setExistingImage] = useState(null);
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Fetch existing theme data
  useEffect(() => {
    const fetchThemeDetails = async () => {
      try {
        const response = await QrThemeService.detail(id);
        setData(response);

        // Set existing image if available
        if (response.image) {
          setExistingImage(
            response.image.startsWith("data:")
              ? response.image
              : `data:image/jpeg;base64,${response.image}`
          );
        }
      } catch (error) {
        console.error("Error fetching theme details:", error);
        toast.error("Failed to fetch theme details");
      }
    };

    if (id) {
      fetchThemeDetails();
    }
  }, [id]);

  // Handle file selection
  const handleAcceptedFiles = (files, setFieldValue) => {
    const formattedFiles = files.map((file) => ({
      ...file,
      preview: URL.createObjectURL(file),
      formattedSize: formatBytes(file.size),
    }));

    setSelectedFiles([...formattedFiles]);
    setExistingImage(null); // Clear existing image when new file is selected

    if (files.length > 0) {
      setFieldValue("image", files[0]);
    }
  };

  // Handle file removal
  const handleFileRemove = (indexToRemove, setFieldValue) => {
    setSelectedFiles((prevFiles) => {
      const newFiles = prevFiles.filter((_, index) => index !== indexToRemove);
      if (newFiles.length === 0) {
        setFieldValue("image", null);
      }
      return newFiles;
    });
  };

  // Form submission handler
  const handleSubmit = async (values, { setSubmitting }) => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("description", values.description);
      formData.append("isActive", values.isActive);
      formData.append("isDefault", values.isDefault);

      // Handle image upload
      if (values.image) {
        // New file uploaded
        formData.append("image", values.image);
      } else if (existingImage) {
        // Keep existing image
        formData.append("existingImage", existingImage);
      }

      // Determine API endpoint and method based on whether it's a new or existing theme
      const apiMethod = id ? "put" : "post";
      const apiUrl = id
        ? `https://api-dev-qrmenu.essencetechnologies.com/api/themes/${id}`
        : "https://api-dev-qrmenu.essencetechnologies.com/api/themes";

      const response = await axios[apiMethod](apiUrl, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Show success message
      toast.success(
        id ? "Theme updated successfully" : "Theme created successfully"
      );

      // Navigate back to theme list
      navigate("/theme");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(error.response?.data?.message || "Failed to save theme");
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb
          pageTitle="Themes"
          title={id ? "Edit Theme" : "Create Theme"}
          breadcrumbItems={[{ title: "Back To List", link: "/theme" }]}
        />
      </div>
      <Formik
        initialValues={{
          title: data?.title || "",
          description: data?.description || "",
          image: data?.image ? `data:image/jpeg;base64,${data.image}` : null,
          isActive: data?.isActive ?? true, // Default to true if not specified
          isDefault: data?.isDefault ?? false, // Default to false if not specified
        }}
        enableReinitialize
        validate={(values) => {
          const errors = {};
          if (!values.title) {
            errors.title = "Theme title is required";
          }
          if (!values.description) {
            errors.description = "Product description is required";
          }
          return errors;
        }}
        onSubmit={handleSubmit}
      >
        {({
          setFieldValue,
          values,
          errors,
          touched,
          handleSubmit,
          isSubmitting,
        }) => (
          <Form onSubmit={handleSubmit}>
            <Card>
              <CardBody>
                <Row>
                  <Col>
                    <div className="mb-3">
                      <Label className="form-label" htmlFor="theme-title-input">
                        Theme Title
                      </Label>
                      <Input
                        type="text"
                        className="form-control"
                        id="theme-title-input"
                        placeholder="Enter theme title"
                        name="title"
                        value={values.title || ""}
                        onChange={(e) => setFieldValue("title", e.target.value)}
                        invalid={touched.title && !!errors.title}
                      />
                      {touched.title && errors.title && (
                        <FormFeedback type="invalid">
                          {errors.title}
                        </FormFeedback>
                      )}
                    </div>

                    <div>
                      <Label>Description</Label>
                      <Input
                        type="textarea"
                        rows="6"
                        className="form-control"
                        id="theme-description-input"
                        placeholder="Enter theme description"
                        name="description"
                        value={values.description || ""}
                        onChange={(e) =>
                          setFieldValue("description", e.target.value)
                        }
                        invalid={touched.description && !!errors.description}
                      />
                      {touched.description && errors.description && (
                        <FormFeedback type="invalid" className="d-block">
                          {errors.description}
                        </FormFeedback>
                      )}
                    </div>
                  </Col>
                  <Col>
                    <Row>
                      <Col md={12} className="d-flex justify-content-end gap-4">
                        {/* isDefault switch */}
                        <div className="form-check form-switch">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            id="isDefaultSwitch"
                            checked={values.isDefault}
                            onChange={() => 
                              setFieldValue("isDefault", !values.isDefault)
                            }
                          />
                          <Label 
                            className="form-check-label" 
                            htmlFor="isDefaultSwitch"
                          >
                            Default
                          </Label>
                        </div>

                        {/* isActive switch */}
                        <div className="form-check form-switch">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            id="isActiveSwitch"
                            checked={values.isActive}
                            onChange={() => 
                              setFieldValue("isActive", !values.isActive)
                            }
                          />
                          <Label 
                            className="form-check-label" 
                            htmlFor="isActiveSwitch"
                          >
                            Active
                          </Label>
                        </div>
                      </Col>
                      <Col md={12}>
                        <Dropzone
                          onDrop={(acceptedFiles) =>
                            handleAcceptedFiles(acceptedFiles, setFieldValue)
                          }
                        >
                          {({ getRootProps, getInputProps }) => (
                            <div className="dropzone dz-clickable pt-3">
                              <div
                                className="dz-message needsclick mb-5 p-3"
                                {...getRootProps()}
                              >
                                <i className="align-item-center display-4 text-muted ri-upload-cloud-2-fill" />
                                <input {...getInputProps()} />
                                <h6 className="fs-15 fw-semi-bold pt-3">
                                  Drop files here or{" "}
                                  <Link to="#">click to upload.</Link>
                                </h6>

                                {/* Show existing image if no new file is selected */}
                                {existingImage && !selectedFiles.length && (
                                  <Card className="mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete">
                                    <Row className="align-items-center">
                                      <Col className="col-auto">
                                        <img
                                          style={{
                                            width: "5rem",
                                            height: "3rem",
                                            objectFit: "cover",
                                          }}
                                          className="avatar-sm rounded bg-light"
                                          alt="Existing theme image"
                                          src={existingImage}
                                        />
                                      </Col>
                                      <Col>
                                        <p className="mb-0 text-muted fs-6">
                                          Existing Image
                                        </p>
                                      </Col>
                                    </Row>
                                  </Card>
                                )}

                                {/* Show newly selected files */}
                                { selectedFiles.map((f, i) => (
                                  <Card
                                    key={i}
                                    className="mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                                  >
                                    <Row className="align-items-center">
                                      <Col className="col-auto">
                                        <img
                                          height="100"
                                          className="avatar-sm rounded bg-light"
                                          alt={f.name}
                                          src={f.preview}
                                        />
                                      </Col>
                                      <Col>
                                        <Link
                                          to="#"
                                          className="text-muted font-weight-bold"
                                        >
                                          {f.name}
                                        </Link>
                                        <p className="mb-0 fs-12">
                                          {f.formattedSize}
                                        </p>
                                      </Col>
                                      <Col lg={2}>
                                        <Link
                                          onClick={() =>
                                            handleFileRemove(i, setFieldValue)
                                          }
                                        >
                                          <h6>
                                            <i className="ri-delete-bin-2-line text-danger"></i>
                                          </h6>
                                        </Link>
                                      </Col>
                                    </Row>
                                  </Card>
                                ))}
                              </div>
                            </div>
                          )}
                        </Dropzone>
                      </Col>
                    </Row>
                  </Col>
                </Row>

                <button
                  type="submit"
                  className="btn btn-primary mt-3"
                  disabled={isSubmitting}
                >
                  {isLoading
                    ? "Saving..."
                    : id
                    ? "Update Theme"
                    : "Create Theme"}
                </button>
              </CardBody>
            </Card>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AddUpdateTheme;